import { Button, DatePicker, Icon, MuiMenuItem, TextField, Typography, strIsNullOrEmpty, useDebounce } from '@fox/atoms';
import { FlexRowSmallSpaced } from '@fox/layout';
import { Pagination } from '@mui/material';
import MUIAlert from '@mui/material/Alert';
import { Moment } from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';
import { capitalizeFirstLetter } from '../../common/helper/helper';
import { useFetch } from '../../common/use-fetch';
import { useContentContext } from '../content-context';
import { CompactView } from './compact-view';
import { GetNewsByTagsInputModel, GetNewsByTagsResultModel, NewsListItemModel, NewsListProps } from './contracts';
import { ExpandedView } from './expanded-view';
import { LeftImageView } from './left-image-view';

const TagsAndFilterWrapper = styled.div<{ alignment: 'Left' | 'Right' | 'Center (default)' }>`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	align-items: ${props => (props.alignment === 'Left' ? 'flex-start' : props.alignment === 'Right' ? 'flex-end' : 'center')};
`;

const ControlsWrapper = styled.div`
	max-width: 970px;
	width: 100%;
	display: flex;
	flex-direction: row;
	gap: 2rem;
	justify-content: space-between;
	flex-wrap: wrap;
	padding-bottom: 1rem;
	padding-right: 2rem;
	padding-left: 2rem;
	.filterControl {
		.MuiAutocomplete-root {
			height: 40px;
			min-height: 40px !important;
			.MuiFormControl-root {
				height: 40px;
				.MuiInputBase-root {
					height: 40px;
					.MuiInputBase-input {
						height: 20px;
					}
				}
			}
		}
	}
	@media (max-width: 970px) {
		justify-content: center;
	}
	@media (max-width: 767px) {
		padding: 0rem 1rem;
		flex-direction: column;
		gap: 1rem;
		.filterGroup {
			flex-direction: column;
			width: 100% !important;
			gap: 1rem;
		}
		.filterControl {
			min-width: 100% !important;
			max-width: 100% !important;
		}
	}
`;

const TagWrapper = styled.div<{ backgroundColor: string }>`
	background-color: ${props => props.backgroundColor};
	padding-top: 2rem;
	padding-bottom: 1rem;
	padding-left: 2rem;
	padding-right: 2rem;
	display: flex;
	row-gap: 0.5rem;
	column-gap: 1.5rem;
	justify-content: center;
	flex-wrap: wrap;
	@media (max-width: 600px) {
		gap: 0.25rem;
	}
`;

const NewsTag = styled(Typography)<{ $isSelected: boolean; $isSelectedBackgroundColor: string }>`
	cursor: pointer;
	padding: 0.5rem 1rem;
	font-size: 1.1rem;
	transition: background-color ease-in-out 0.3s;
	width: max-content;
	background-color: ${props =>
		props.$isSelectedBackgroundColor && props.$isSelected ? props.$isSelectedBackgroundColor : props.$isSelected ? 'black' : 'none'};
	border-radius: 5px;
	border: ${props => (strIsNullOrEmpty(props.$isSelectedBackgroundColor) ? '1px solid black' : 'none')};
	${props => (props.$isSelected && strIsNullOrEmpty(props.$isSelectedBackgroundColor) ? 'color: white;' : '')}
	@media (max-width: 600px) {
		font-size: 1rem;
		padding: 0.25rem 0.5rem;
	}
`;

export const PaginationWrapper = styled.div`
	width: 100%;
	justify-content: center;
	display: flex;
	padding-top: 1rem;
	padding-bottom: 1rem;
`;

export const PageSizeOptionsWrapper = styled.div`
	padding: 1rem 0rem;
	display: flex;
	justify-content: center;
`;

function NewsRender({
	display,
	openLinkInNewTab,
	hideThumbnail,
	newsItems
}: {
	display: string;
	openLinkInNewTab: boolean;
	newsItems: NewsListItemModel[];
	hideThumbnail?: boolean;
}) {
	switch (display) {
		case 'Compact':
			return <CompactView openLinkInNewTab={openLinkInNewTab} newsItems={newsItems} hideThumbnail={hideThumbnail} />;
		case 'LeftImage':
			return <LeftImageView openLinkInNewTab={openLinkInNewTab} newsItems={newsItems} hideThumbnail={hideThumbnail} />;
		default:
			return <ExpandedView openLinkInNewTab={openLinkInNewTab} newsItems={newsItems} hideThumbnail={hideThumbnail} />;
	}
}

export const NewsList: React.FC<NewsListProps> = ({
	openLinkInNewTab,
	tags,
	rSSFeedUrl,
	selectedTagHighlightColour,
	tagSelectorBackgroundColour,
	displayMode,
	hideThumbnail,
	filterAlignment
}) => {
	const pageSizeOptions = displayMode === 'Compact' ? [9, 18, 27] : [8, 16, 24];
	const { contentId } = useContentContext();
	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	const [page, setSelectedPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(pageSizeOptions[0]);
	const [search, setSearch] = useState<string>('');
	const [from, setFrom] = useState<Date | null>(null);
	const [to, setTo] = useState<Date | null>(null);
	const [sortOldToNew, setSortOldToNew] = useState<'Newest First' | 'Oldest First'>('Newest First');
	const debouncedSearch = useDebounce(search, 500);

	const { loading, error, result } = useFetch<GetNewsByTagsResultModel, GetNewsByTagsInputModel>(
		{
			page,
			pageSize: Number(pageSize),
			contentId,
			tags: selectedTags,
			rSSFeedUrl,
			from,
			search: debouncedSearch,
			sortOldToNew: sortOldToNew === 'Oldest First' ? true : false,
			to
		},
		'/Umbraco/Api/NewsSearch/GetNewsByTags',
		'POST',
		['tags', 'page', 'pageSize', 'search', 'from', 'to', 'sortOldToNew']
	);

	if (error) return <MUIAlert severity="error">{error}</MUIAlert>;

	return (
		<div>
			<TagsAndFilterWrapper alignment={filterAlignment}>
				{tags?.length > 0 ? (
					<TagWrapper backgroundColor={tagSelectorBackgroundColour || 'none'}>
						<NewsTag
							variant="h5"
							$isSelected={selectedTags.length < 1}
							$isSelectedBackgroundColor={selectedTagHighlightColour}
							key="see-all"
							onClick={() => {
								setSelectedTags([]);
								setSelectedPage(1);
							}}
						>
							See all
						</NewsTag>

						{tags.map(tag => {
							const isSelected = selectedTags.includes(tag);
							return (
								<NewsTag
									variant="h5"
									$isSelected={isSelected}
									$isSelectedBackgroundColor={selectedTagHighlightColour}
									key={tag}
									onClick={() => {
										if (isSelected) {
											setSelectedTags(prev => prev.filter(t => t !== tag));
											setSelectedPage(1);
										} else {
											setSelectedTags(prev => [...prev, tag]);
											setSelectedPage(1);
										}
									}}
								>
									{capitalizeFirstLetter(tag)}
								</NewsTag>
							);
						})}
					</TagWrapper>
				) : null}
				<ControlsWrapper>
					<TextField
						className="filterControl"
						value={search ?? ''}
						onChange={e => setSearch(e.target.value)}
						autoFocus
						sx={{ minWidth: '10rem', maxWidth: '15rem' }}
						size="small"
						placeholder="Search"
						endAdornment={<Icon iconName="Search" />}
						inputProps={{
							style: { paddingLeft: 10, paddingRight: 15 }
						}}
					/>
					<FlexRowSmallSpaced className="filterGroup">
						<DatePicker
							className="filterControl"
							label="Date from"
							onChange={(value: Moment) => setFrom(value.toDate())}
							size="small"
							sx={{ minWidth: '10rem', maxWidth: '10rem' }}
						/>
						<DatePicker
							className="filterControl"
							label="Date to"
							onChange={(value: Moment) => setTo(value.toDate())}
							size="small"
							sx={{ minWidth: '10rem', maxWidth: '10rem' }}
						/>
					</FlexRowSmallSpaced>

					<FlexRowSmallSpaced className="filterGroup">
						<TextField
							className="filterControl"
							size="small"
							label="Sort by"
							sx={{ minWidth: '10rem', maxWidth: '10rem' }}
							value={sortOldToNew}
							select
							onChange={e => {
								if (e.target.value === 'Newest First' || e.target.value === 'Oldest First') setSortOldToNew(e.target.value);
							}}
						>
							<MuiMenuItem value="Newest First">Newest First</MuiMenuItem>
							<MuiMenuItem value="Oldest First">Oldest First</MuiMenuItem>
						</TextField>

						<Button
							sx={{ padding: '0.25rem 0.5rem', fontSize: '1.1rem' }}
							disabled={
								strIsNullOrEmpty(search) &&
								from === null &&
								to === null &&
								!selectedTags.length &&
								sortOldToNew === 'Newest First'
							}
							onClick={() => {
								setSearch('');
								setFrom(null);
								setTo(null);
								setSortOldToNew('Newest First');
								setSelectedTags([]);
							}}
							variant="secondary"
						>
							Reset
						</Button>
					</FlexRowSmallSpaced>
				</ControlsWrapper>
			</TagsAndFilterWrapper>
			{!!result?.newsItems?.length ? (
				<NewsRender
					display={displayMode}
					openLinkInNewTab={openLinkInNewTab}
					newsItems={result?.newsItems}
					hideThumbnail={hideThumbnail}
				/>
			) : (
				<MUIAlert sx={{ maxWidth: '40rem', margin: '1rem auto' }} severity="info">
					No news items to display
				</MUIAlert>
			)}
			{result?.totalPages > 1 ? (
				<PaginationWrapper>
					<Pagination
						style={{ alignSelf: 'center' }}
						count={result.totalPages}
						page={page}
						onChange={(_, value) => {
							setSelectedPage(value);
						}}
						color={'secondary'}
					/>
				</PaginationWrapper>
			) : null}
			<PageSizeOptionsWrapper>
				<TextField
					size="small"
					sx={{ width: '150px' }}
					label="Items per page"
					value={pageSize}
					select
					onChange={e => {
						setPageSize(Number(e.target.value));
						setSelectedPage(1);
					}}
				>
					{pageSizeOptions.map(option => (
						<MuiMenuItem value={option} key={option}>
							{option}
						</MuiMenuItem>
					))}
				</TextField>
			</PageSizeOptionsWrapper>
		</div>
	);
};
